/*
 * @Description: 实时定位驾驶舱 — A1「状态统计」数据 hook
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '../../../app/hooks'
import { fetchRealtimeSummary } from '../../../actions/locationCockpit'
import { REALTIME_POLL_INTERVAL_MS, isCockpitMockEnabled } from '../constants'
import {
  MOCK_SUMMARY_HISTORY,
  MOCK_SUMMARY_REALTIME
} from '../mock/summary.mock'
import { CockpitEnvelope, RealtimeSummaryData } from '../types/cockpit.types'

export interface UseRealtimeSummaryResult {
  /** 状态统计；未取到时为 null（页面按空处理） */
  summary: RealtimeSummaryData | null
  loading: boolean
  /** 手动刷新一次 */
  refresh: () => void
}

/**
 * 拉取实时定位驾驶舱 A1「状态统计」。
 * 实时态（当天）按 60s 轮询；历史态（非当天）只取一次，不轮询。
 * mock 模式按 isToday 返回实时态 / 历史态 fixtures，不发请求。
 *
 * @param groupId - 当前团队长 id
 * @param date - 查询日期 YYYY-MM-DD
 * @param isToday - date 是否为当天（决定实时态/历史态与是否轮询）
 */
export function useRealtimeSummary(
  groupId: string,
  date: string,
  isToday: boolean,
  pollIntervalMs: number = REALTIME_POLL_INTERVAL_MS
): UseRealtimeSummaryResult {
  const dispatch = useAppDispatch()
  const isMock = isCockpitMockEnabled()
  const [summary, setSummary] = useState<RealtimeSummaryData | null>(null)
  const [loading, setLoading] = useState(false)
  const aliveRef = useRef(true)

  const load = useCallback(() => {
    if (isMock) {
      setSummary(isToday ? MOCK_SUMMARY_REALTIME : MOCK_SUMMARY_HISTORY)
      return
    }
    if (!groupId) return

    setLoading(true)
    dispatch(fetchRealtimeSummary({ groupId, date }))
      .then((res: CockpitEnvelope<RealtimeSummaryData>) => {
        if (!aliveRef.current) return
        setLoading(false)
        if (res && res.code === 200) {
          setSummary(res.data ?? null)
        }
      })
      .catch(() => {
        if (!aliveRef.current) return
        setLoading(false)
      })
  }, [dispatch, groupId, date, isMock, isToday])

  useEffect(() => {
    aliveRef.current = true
    load()

    // 仅实时态轮询；mock 与历史态不轮询
    if (isMock || !isToday) {
      return () => {
        aliveRef.current = false
      }
    }

    const timer = window.setInterval(load, pollIntervalMs)
    return () => {
      aliveRef.current = false
      window.clearInterval(timer)
    }
  }, [load, isMock, isToday, pollIntervalMs])

  return { summary, loading, refresh: load }
}
